import { LockBlock as TiptapLockBlock, type LockBlockOptions } from "@corca/tiptap";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { AttrStep, ReplaceAroundStep, ReplaceStep } from "@tiptap/pm/transform";
import { ySyncPluginKey } from "@tiptap/y-tiptap";

import {
  collectLockedRootIds,
  isInLockedTopBlock,
  isLockable,
  isLocked,
  rangeTouchesLockedBlock,
} from "./utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import type { Step } from "@tiptap/pm/transform";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    lockBlock: {
      /**
       * Set the locked state of the lockable top-level block at `pos`.
       */
      setLocked: (pos: number, locked: boolean) => ReturnType;
    };
  }
}

const LOCK_TOGGLE_META = "lockBlockToggle";

const lockGuardPluginKey = new PluginKey("lockBlockGuard");

const lockedRootsPluginKey = new PluginKey("lockBlockLockedRoots");

/** Attributes that may still change on a locked block (collapse/expand, the lock toggle itself). */
const ALLOWED_LOCKED_ATTRS = new Set(["collapsed", "locked"]);

type Options = LockBlockOptions & { doc?: DocumentModel };

/**
 * Client-enforced soft guard against accidental edits, like the document lock.
 * Math expression edits bypass ProseMirror and are gated in the MobX layer via
 * `EditorModel.lockedRootIds`, which this extension keeps in sync with the PM doc.
 */
export const LockBlock = TiptapLockBlock.extend<Options>({
  addCommands() {
    return {
      setLocked:
        (pos, locked) =>
        ({ tr, dispatch }) => {
          const node = tr.doc.nodeAt(pos);

          if (!node || !isLockable(node) || tr.doc.resolve(pos).depth !== 0) {
            return false;
          }

          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, locked });
            tr.setMeta(LOCK_TOGGLE_META, true);
          }

          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // The guard vetoes splitting a locked block, so Enter at its very start/end
      // inserts a fresh paragraph next to it instead of silently doing nothing.
      Enter: () => {
        const { selection } = this.editor.state;

        if (!selection.empty) {
          return false;
        }

        const { $from } = selection;

        if (!isInLockedTopBlock($from)) {
          return false;
        }

        const atStart = $from.pos - $from.before(1) === $from.depth;
        const atEnd = $from.after(1) - $from.pos === $from.depth;

        if (!atStart && !atEnd) {
          return false;
        }

        const pos = atEnd ? $from.after(1) : $from.before(1);

        return this.editor
          .chain()
          .insertContentAt(pos, { type: "paragraph" })
          .setTextSelection(pos + 1)
          .run();
      },

      // The guard vetoes joinBackward into a locked block, which would also swallow
      // deleting an empty line right after one. Removing just that line touches
      // nothing locked, so do it here and land the cursor at the locked block's end.
      Backspace: () => {
        const { doc, selection } = this.editor.state;

        if (!selection.empty) {
          return false;
        }

        const { $from } = selection;

        if ($from.depth === 0 || isInLockedTopBlock($from)) {
          return false;
        }

        const atStart = $from.pos - $from.before(1) === $from.depth;
        const blockIndex = $from.index(0);

        if (
          !atStart ||
          $from.node(1).content.size !== 0 ||
          blockIndex === 0 ||
          !isLocked(doc.child(blockIndex - 1))
        ) {
          return false;
        }

        return this.editor.commands.command(({ tr, dispatch }) => {
          if (dispatch) {
            tr.delete($from.before(1), $from.after(1));
            tr.setSelection(TextSelection.near(tr.doc.resolve($from.before(1)), -1));
          }

          return true;
        });
      },
    };
  },

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin({
        key: lockGuardPluginKey,
        filterTransaction: (tr) => filterLockedEdits(tr),
      }),
      ...(doc
        ? [
            new Plugin({
              key: lockedRootsPluginKey,
              view: (view) => {
                syncLockedRootIds(doc, view.state);

                return {
                  update: (view, prevState) => {
                    if (view.state.doc !== prevState.doc) {
                      syncLockedRootIds(doc, view.state);
                    }
                  },
                };
              },
            }),
          ]
        : []),
    ];
  },
});

function syncLockedRootIds(doc: DocumentModel, state: EditorState): void {
  const next = collectLockedRootIds(state.doc);
  const current = doc.editor.lockedRootIds;

  if (next.size === current.size && [...next].every((id) => current.has(id))) {
    return;
  }

  doc.editor.setLockedRootIds(next);
}

function filterLockedEdits(tr: Transaction): boolean {
  if (!tr.docChanged) {
    return true;
  }

  // Remote changes and yjs undo/redo must pass, or the editor diverges from the Y.Doc.
  const ySyncMeta = tr.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean } | undefined;
  if (ySyncMeta?.isChangeOrigin) {
    return true;
  }

  if (tr.getMeta(LOCK_TOGGLE_META)) {
    return true;
  }

  return !tr.steps.some((step, i) => stepTouchesLockedBlock(step, tr.docs[i]));
}

function stepTouchesLockedBlock(step: Step, docBefore: PMNode): boolean {
  if (isAllowedAttrOnlyStep(step, docBefore)) {
    return false;
  }

  if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
    let touches = false;

    step.getMap().forEach((from, to) => {
      if (!touches) {
        touches = rangeTouchesLockedBlock(docBefore, from, to);
      }
    });

    return touches;
  }

  // Mark steps and other position-carrying steps have empty step maps; check spans explicitly.
  const withRange = step as Step & { from?: number; to?: number; pos?: number };

  if (typeof withRange.from === "number" && typeof withRange.to === "number") {
    return rangeTouchesLockedBlock(docBefore, withRange.from, withRange.to);
  }

  // `pos` may point at the locked top-level block itself (depth 0), not only inside it.
  if (typeof withRange.pos === "number") {
    return (
      isLocked(docBefore.nodeAt(withRange.pos)) ||
      isInLockedTopBlock(docBefore.resolve(withRange.pos))
    );
  }

  return false;
}

/**
 * Matches a `setNodeMarkup`-shaped step (`ReplaceAroundStep`, or `ReplaceStep` for leaf
 * nodes) or `AttrStep` whose only attr changes are in `ALLOWED_LOCKED_ATTRS`.
 */
function isAllowedAttrOnlyStep(step: Step, docBefore: PMNode): boolean {
  if (step instanceof AttrStep) {
    return ALLOWED_LOCKED_ATTRS.has(step.attr);
  }

  if (!(step instanceof ReplaceAroundStep) && !(step instanceof ReplaceStep)) {
    return false;
  }

  const replacement = step.slice.content.firstChild;

  if (!replacement || step.slice.content.childCount !== 1) {
    return false;
  }

  if (step instanceof ReplaceAroundStep) {
    if (step.gapFrom !== step.from + 1 || step.gapTo !== step.to - 1) {
      return false;
    }
  } else if (step.slice.openStart !== 0 || step.slice.openEnd !== 0) {
    return false;
  }

  const node = docBefore.nodeAt(step.from);

  if (
    !node ||
    replacement.type !== node.type ||
    step.to !== step.from + node.nodeSize ||
    (step instanceof ReplaceStep && !node.isLeaf)
  ) {
    return false;
  }

  return Object.keys({ ...node.attrs, ...replacement.attrs }).every(
    (key) => node.attrs[key] === replacement.attrs[key] || ALLOWED_LOCKED_ATTRS.has(key),
  );
}
